import { setToken, getToken } from './sync'

/**
 * L'entrée dans l'app — décision 2026-08-24.
 *
 * C'est le SEUL moment où Ariariko exige le réseau. Une fois le téléphone
 * prouvé, le jeton vit sur l'appareil et l'app redevient utilisable à 100 %
 * hors ligne, pour toujours : rouvrir l'app ne redemande jamais de se
 * connecter. Le réseau ne sert plus qu'à la synchronisation, en arrière-plan.
 *
 * ⚠️ Ce module est le seul du client à parler au serveur pour AFFICHER quelque
 * chose. C'est l'exception assumée à l'offline-first, au même titre que le
 * panneau d'administration — et elle ne dure que le temps de l'inscription.
 */

const API = import.meta.env?.VITE_API_URL ?? '/api/v1'

const PROFILE = 'ariariko:account'

export interface AccountProfile {
  id: string
  first_name: string
  last_name: string
  birthdate: string | null
  phone: string
  email: string | null
  phone_verified: boolean
  email_verified: boolean
  /** Depuis quand ce compte existe — le seul repère d'ancienneté à l'écran. */
  created_at: string | null
  /**
   * Les portefeuilles que le compte possède DÉJÀ, le plus ancien d'abord.
   *
   * C'est ce qui rend le multi-appareil possible : sans cette liste, un
   * second appareil n'a aucun moyen de savoir qu'un portefeuille existe, et
   * en crée un deuxième, vide (voir `bootstrap.ts`). Optionnel : un profil
   * enregistré par une version antérieure ne le porte pas.
   */
  wallet_ids?: string[]
}

export function getProfile(): AccountProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE)
    return raw ? (JSON.parse(raw) as AccountProfile) : null
  } catch { return null }
}

function setProfile(profile: AccountProfile) {
  try { localStorage.setItem(PROFILE, JSON.stringify(profile)) } catch { /* privée */ }
}

/**
 * L'appareil est-il déjà entré ?
 *
 * Le JETON et non le profil : c'est lui qui ouvre la synchronisation, et un
 * profil sans jeton ne servirait à rien.
 */
export function isSignedIn(): boolean {
  return getToken() !== null
}

/** Ce que le serveur a répondu, sans jamais lever : le réseau échoue, c'est normal. */
export type AuthResult<T> = { ok: true; data: T } | { ok: false; error: string }

async function post<T>(path: string, body: unknown): Promise<AuthResult<T>> {
  let response: Response
  try {
    response = await fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    // Distinguer « pas de réseau » d'un refus du serveur : le geste à faire
    // n'est pas le même, et « une erreur est survenue » n'aide personne.
    return { ok: false, error: 'Pas de connexion. Vérifie ton réseau et réessaie.' }
  }

  let payload: Record<string, unknown> = {}
  try { payload = await response.json() } catch { /* corps vide */ }

  if (!response.ok) {
    const details = payload.details as Record<string, string[]> | undefined
    // Les erreurs de champ du serveur valent mieux qu'un message générique :
    // elles disent QUEL champ ne va pas.
    const first = details && Object.values(details)[0]?.[0]
    return { ok: false, error: (payload.error as string) ?? first ?? 'Réessaie dans un instant.' }
  }
  return { ok: true, data: payload as T }
}

export interface SignupInput {
  first_name: string
  last_name: string
  birthdate: string
  phone: string
  password: string
  email?: string
}

/** Crée le compte et déclenche l'envoi du code. Le compte n'est pas encore ouvert. */
export function signup(input: SignupInput) {
  return post<{ account_id: string; phone: string; next: string }>('/auth/signup', input)
}

/** (Re)demande un code de vérification. Il PROUVE une destination, il n'ouvre rien. */
export function requestCode(identifier: string, channel: 'sms' | 'email' = 'sms') {
  return post<{ sent: boolean }>('/auth/code', { identifier, channel })
}

/**
 * L'entrée ordinaire — décision 2026-08-24 (soir).
 *
 * Le mot de passe ouvre la session, l'OTP ne sert plus qu'à prouver le numéro.
 * Un SMS par connexion coûterait de l'argent à chaque ouverture de l'app, et
 * exigerait du réseau là où Ariariko promet de s'en passer.
 *
 * `identifier` est le téléphone OU l'email : le serveur reconnaît les deux, et
 * obliger l'utilisateur à choisir dans un menu serait une question de plus pour
 * rien.
 */
export async function login(
  identifier: string, password: string,
): Promise<AuthResult<{ session: boolean; profile: AccountProfile | null }>> {
  const result = await post<{
    token?: string
    account?: AccountProfile
    session?: boolean
  }>('/auth/login', {
    identifier, password,
    platform: 'web',
    device_name: navigator.userAgent.slice(0, 80),
  })
  if (!result.ok) return result
  return { ok: true, data: adopt(result.data) }
}

/**
 * Échange le code contre un jeton d'appareil.
 *
 * Le jeton est posé ICI, et c'est le seul endroit de l'app qui le fait. Tant
 * que le téléphone n'est pas prouvé, le serveur répond sans jeton — vérifier
 * son email ne suffit pas à ouvrir une session.
 */
export async function verifyCode(
  identifier: string, code: string, channel: 'sms' | 'email' = 'sms',
): Promise<AuthResult<{ session: boolean; profile: AccountProfile | null }>> {
  const result = await post<{
    token?: string
    account?: AccountProfile
    session?: boolean
  }>('/auth/verify', {
    identifier, code, channel,
    platform: 'web',
    device_name: navigator.userAgent.slice(0, 80),
  })
  if (!result.ok) return result

  return { ok: true, data: adopt(result.data) }
}

/**
 * Mot de passe oublié — vérifie un code déjà émis par `requestCode` et pose
 * le nouveau mot de passe. Ouvre une session au passage, comme `verifyCode`,
 * mais seulement si le téléphone a déjà été prouvé : réinitialiser par email
 * ne prouve que l'email, jamais le numéro — la même règle que partout ailleurs.
 */
export async function resetPassword(
  identifier: string, code: string, password: string,
): Promise<AuthResult<{ session: boolean; profile: AccountProfile | null }>> {
  const result = await post<{
    token?: string
    account?: AccountProfile
    session?: boolean
  }>('/auth/password', {
    identifier, code, password,
    platform: 'web',
    device_name: navigator.userAgent.slice(0, 80),
  })
  if (!result.ok) return result

  return { ok: true, data: adopt(result.data) }
}

/**
 * Pose le jeton et le profil quand le serveur en a rendu.
 *
 * Écrit ICI et nulle part ailleurs : deux endroits qui posent un jeton, c'est
 * un jour où l'un des deux oublie le profil.
 */
function adopt(payload: { token?: string; account?: AccountProfile }) {
  if (!payload.token || !payload.account) return { session: false, profile: null }
  setToken(payload.token)
  setProfile(payload.account)
  return { session: true, profile: payload.account }
}

/**
 * Quitter l'appareil.
 *
 * Le jeton et le profil s'en vont, **les données locales restent**. Se
 * déconnecter n'est pas se supprimer : quelqu'un qui prête son téléphone doit
 * pouvoir sortir sans perdre son budget. La file d'envoi repartira à la
 * prochaine connexion.
 */
export function signOut() {
  try {
    localStorage.removeItem('ariariko:token')
    localStorage.removeItem(PROFILE)
  } catch { /* privée */ }
}

/* ─────────────────────────────────────────────────────────────────────────
   Gérer son compte — décision 2026-08-24 (soir)

   Authentifié par le JETON D'APPAREIL, comme la synchronisation. Ces appels
   sont les SEULS de l'app, avec le portail, à exiger le réseau pour afficher
   quelque chose — et ils sont bornés aux réglages, jamais à l'argent.
   ───────────────────────────────────────────────────────────────────────── */

function authed(): HeadersInit {
  const token = getToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function send<T>(
  path: string, method: string, body?: unknown,
): Promise<AuthResult<T>> {
  let response: Response
  try {
    response = await fetch(`${API}${path}`, {
      method, headers: authed(),
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    return { ok: false, error: 'Pas de connexion. Vérifie ton réseau et réessaie.' }
  }

  let payload: Record<string, unknown> = {}
  try { payload = await response.json() } catch { /* corps vide */ }

  if (!response.ok) {
    const details = payload.details as Record<string, string[]> | undefined
    const first = details && Object.values(details)[0]?.[0]
    return { ok: false, error: (payload.error as string) ?? first ?? 'Réessaie dans un instant.' }
  }
  return { ok: true, data: payload as T }
}

/** Le profil, relu au serveur — la vérité sur l'état des vérifications. */
export async function fetchProfile(): Promise<AuthResult<AccountProfile>> {
  const result = await send<{ account: AccountProfile }>('/account', 'GET')
  if (result.ok) setProfile(result.data.account)
  return result.ok ? { ok: true, data: result.data.account } : result
}

/** Nom, prénom, date de naissance. Jamais les identifiants — ils se prouvent. */
export async function updateIdentity(
  input: { first_name: string; last_name: string; birthdate: string },
): Promise<AuthResult<AccountProfile>> {
  const result = await send<{ account: AccountProfile }>('/account', 'PATCH', input)
  if (result.ok) setProfile(result.data.account)
  return result.ok ? { ok: true, data: result.data.account } : result
}

/** L'ancien mot de passe est exigé, même avec une session valide. */
export function changePassword(current: string, password: string) {
  return send<{ changed: boolean }>('/account/password', 'PATCH', { current, password })
}

/** Change le numéro ET le remet à prouver : un code part aussitôt. */
export async function changePhone(phone: string): Promise<AuthResult<AccountProfile>> {
  const result = await send<{ account: AccountProfile }>(
    '/account/phone', 'POST', { phone: normalizePhone(phone) })
  if (result.ok) setProfile(result.data.account)
  return result.ok ? { ok: true, data: result.data.account } : result
}

/** Change l'email et le remet à confirmer. Une chaîne vide le retire. */
export async function changeEmail(email: string): Promise<AuthResult<AccountProfile>> {
  const result = await send<{ account: AccountProfile }>('/account/email', 'POST', { email })
  if (result.ok) setProfile(result.data.account)
  return result.ok ? { ok: true, data: result.data.account } : result
}

/** Fermer son compte pour de bon. Le mot de passe est redemandé. */
export function closeAccount(password: string) {
  return send<{ closed: boolean }>('/account', 'DELETE', { password })
}

/**
 * Normalise un numéro malgache pour l'AFFICHAGE et l'envoi.
 *
 * Le serveur normalise de son côté — c'est lui l'autorité — mais laisser partir
 * « 034 12 345 67 » tel quel oblige l'utilisateur à deviner le format attendu.
 * Ici on nettoie ce qui est décoratif, sans jamais deviner un format inconnu.
 */
export function normalizePhone(value: string): string {
  const raw = value.replace(/[\s.\-()]/g, '')
  const digits = /^\+261(\d{9})$/.exec(raw)?.[1]
    ?? /^261(\d{9})$/.exec(raw)?.[1]
    ?? /^0(\d{9})$/.exec(raw)?.[1]
    ?? /^(\d{9})$/.exec(raw)?.[1]
  return digits ? `+261${digits}` : raw
}

/**
 * Un numéro malgache reconnaissable, avant même d'appeler le serveur.
 *
 * ⚠️ Resserré le 2026-09-05 : il suffisait avant d'avoir NEUF chiffres après
 * `+261`, quel qu'en soit le premier. « 0912345678 » passait donc, alors
 * qu'aucun opérateur malgache n'attribue ça. La règle est désormais celle que
 * l'utilisateur connaît — dix chiffres, commençant par `03` — et le serveur
 * l'applique aussi (`Account::PHONE_FORMAT`), car lui seul fait autorité.
 */
export function looksLikePhone(value: string): boolean {
  return /^\+2613\d{8}$/.test(normalizePhone(value))
}
